import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type ModuleRow = {
  id: number;
  code: string;
  name: string;
  plugin_id: string | null;
  description: string | null;
  icon: string | null;
  price_monthly: number;
  price_annual: number;
  is_sellable: boolean;
  sort_order: number;
};

const EMPTY_FORM = {
  code: '',
  name: '',
  plugin_id: '',
  description: '',
  icon: '',
  price_monthly: '0.00',
  price_annual: '0.00',
  is_sellable: true,
  sort_order: '0',
};

export function MasterModulesPage() {
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  async function load() {
    const res = await apiFetch('/api/v1/master/modules');
    const body = (await res.json().catch(() => ({}))) as {
      modules?: ModuleRow[];
      message?: string;
      error?: string;
    };
    if (!res.ok) throw new Error(body.message || body.error || `modules ${res.status}`);
    setModules(
      (Array.isArray(body.modules) ? body.modules : []).map((m) => ({
        ...m,
        price_monthly: Number(m.price_monthly) || 0,
        price_annual: Number(m.price_annual) || 0,
      })),
    );
  }

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  function startEdit(row: ModuleRow) {
    setEditingId(row.id);
    setForm({
      code: row.code,
      name: row.name,
      plugin_id: row.plugin_id || '',
      description: row.description || '',
      icon: row.icon || '',
      price_monthly: Number(row.price_monthly).toFixed(2),
      price_annual: Number(row.price_annual).toFixed(2),
      is_sellable: row.is_sellable,
      sort_order: String(row.sort_order || 0),
    });
    setError(null);
    setMessage(null);
  }

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const payload = {
        name: form.name.trim(),
        plugin_id: form.plugin_id.trim() || undefined,
        description: form.description.trim() || undefined,
        icon: form.icon.trim() || undefined,
        price_monthly: Number(String(form.price_monthly).replace(',', '.')),
        price_annual: Number(String(form.price_annual).replace(',', '.')),
        is_sellable: form.is_sellable,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editingId != null) {
        const res = await apiFetch(`/api/v1/master/modules/${editingId}`, {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
        setMessage(`Modulo aggiornato: ${form.code}`);
      } else {
        const res = await apiFetch('/api/v1/master/modules', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ...payload, code: form.code.trim().toLowerCase() }),
        });
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
        setMessage(`Modulo creato: ${form.code.trim().toLowerCase()}`);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(row: ModuleRow) {
    if (!window.confirm(`Eliminare il modulo «${row.code}»?`)) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await apiFetch(`/api/v1/master/modules/${row.id}`, { method: 'DELETE' });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      setMessage(`Modulo eliminato: ${row.code}`);
      if (editingId === row.id) resetForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Moduli (plugin)"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · Catalogo moduli acquistabili dai tenant
          </>
        }
      />

      {error ? <p className="sk-error">{error}</p> : null}
      {message ? <p className="sk-ok">{message}</p> : null}

      <SectionCard title={`Catalogo (${modules.length})`}>
        <DataTable
          rows={modules}
          emptyMessage="Nessun modulo in catalogo"
          columns={[
            { key: 'code', label: 'Code' },
            { key: 'name', label: 'Nome' },
            { key: 'plugin_id', label: 'Plugin' },
            {
              key: 'price_monthly',
              label: 'Mensile €',
              render: (v) => `€ ${Number(v || 0).toFixed(2)}`,
            },
            {
              key: 'price_annual',
              label: 'Annuale €',
              render: (v) => `€ ${Number(v || 0).toFixed(2)}`,
            },
            {
              key: 'is_sellable',
              label: 'Vendibile',
              render: (v) => (v ? 'Sì' : 'No'),
            },
            {
              key: 'actions',
              label: '',
              render: (_v, row) => (
                <span style={{ display: 'inline-flex', gap: '0.35rem' }}>
                  <button
                    type="button"
                    className="sk-btn sk-btn--quiet"
                    disabled={busy}
                    onClick={() => startEdit(row)}
                  >
                    Modifica
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--quiet"
                    disabled={busy}
                    onClick={() => void onDelete(row)}
                  >
                    Elimina
                  </button>
                </span>
              ),
            },
          ]}
        />
      </SectionCard>

      <SectionCard title={editingId != null ? `Modifica #${editingId}` : 'Nuovo modulo'}>
        <form className="sk-saas-form" style={{ marginTop: '0.85rem', maxWidth: '42rem' }} onSubmit={onSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <label className="sk-field">
              <span>code</span>
              <input
                className="sk-input"
                value={form.code}
                disabled={editingId != null || busy}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                placeholder="gym-base"
                required={editingId == null}
              />
            </label>
            <label className="sk-field">
              <span>Nome</span>
              <input
                className="sk-input"
                value={form.name}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Gym Base"
                required
              />
            </label>
            <label className="sk-field">
              <span>Plugin ID</span>
              <input
                className="sk-input"
                value={form.plugin_id}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, plugin_id: e.target.value }))}
                placeholder="gym"
              />
            </label>
            <label className="sk-field">
              <span>Icona</span>
              <input
                className="sk-input"
                value={form.icon}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                placeholder="dumbbell"
              />
            </label>
            <label className="sk-field">
              <span>Prezzo mensile (€)</span>
              <input
                className="sk-input"
                type="number"
                min={0}
                step={0.01}
                value={form.price_monthly}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, price_monthly: e.target.value }))}
              />
            </label>
            <label className="sk-field">
              <span>Prezzo annuale (€)</span>
              <input
                className="sk-input"
                type="number"
                min={0}
                step={0.01}
                value={form.price_annual}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, price_annual: e.target.value }))}
              />
            </label>
            <label className="sk-field">
              <span>Sort order</span>
              <input
                className="sk-input"
                type="number"
                min={0}
                value={form.sort_order}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
              />
            </label>
            <label className="sk-field" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={form.is_sellable}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, is_sellable: e.target.checked }))}
              />
              <span>Vendibile nello store</span>
            </label>
          </div>
          <label className="sk-field" style={{ marginTop: '0.75rem' }}>
            <span>Descrizione</span>
            <textarea
              className="sk-input"
              value={form.description}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Soci, check-in, abbonamenti, coach, schede..."
              rows={2}
            />
          </label>
          <div className="sk-saas-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button className="sk-btn" type="submit" disabled={busy}>
              {busy ? 'Salvataggio…' : editingId != null ? 'Salva modifiche' : 'Crea modulo'}
            </button>
            {editingId != null ? (
              <button
                type="button"
                className="sk-btn sk-btn--quiet"
                disabled={busy}
                onClick={resetForm}
              >
                Annulla
              </button>
            ) : null}
          </div>
        </form>
      </SectionCard>
    </section>
  );
}
